import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { validateForm, VALIDATION_RULES, ErrorMessage } from '../../utils/formValidation';
import userService from '../../api/services/userService';
import {
  User,
  ArrowLeft,
  Save
} from 'lucide-react';
import Button from '../../components/common/Button/Button';

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Form states
  const [generalSettings, setGeneralSettings] = useState({
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ')[1] || '',
    email: user?.email || '',
    phone: ''
  });

  const [generalErrors, setGeneralErrors] = useState({});

  // Initialize settings from user data
  useEffect(() => {
    // Fetch actual user data from API
    if (user) {
      setGeneralSettings(prev => ({
        ...prev,
        firstName: user?.firstName || user?.name?.split(' ')[0] || '',
        lastName: user?.lastName || user?.name?.split(' ')[1] || '',
        email: user?.email || '',
        phone: user?.phone || user?.mobile || ''
      }));
    }
  }, [user]);

  const handleGeneralChange = (e) => {
    const { name, value } = e.target;

    // Handle different input types
    let processedValue = value;
    if (name === 'phone') {
      // Remove all non-numeric characters
      processedValue = value.replace(/\D/g, '');
    } else if (name === 'firstName' || name === 'lastName') {
      // Remove all non-letter characters (allow letters, spaces, hyphens, apostrophes)
      processedValue = value.replace(/[^a-zA-Z\s\-']/g, '');
    }

    setGeneralSettings(prev => ({
      ...prev,
      [name]: processedValue
    }));
    setHasChanges(true);

    // Clear error when user starts typing
    if (generalErrors[name]) {
      setGeneralErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleGeneralKeyDown = (e) => {
    const { name } = e.target;

    // Allow navigation and control keys for all fields
    if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }

    if (name === 'phone') {
      // Prevent non-numeric characters for phone field
      if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
      }
    } else if (name === 'firstName' || name === 'lastName') {
      // Prevent numbers and special characters for name fields
      // Allow: letters (A-Z, a-z), space (32), hyphen (189), apostrophe (222)
      if (!((e.keyCode >= 65 && e.keyCode <= 90) || // A-Z
        (e.keyCode >= 97 && e.keyCode <= 122) || // a-z
        e.keyCode === 32 || // space
        e.keyCode === 189 || // hyphen
        e.keyCode === 222)) { // apostrophe
        e.preventDefault();
      }
    }
  };


  const isFieldEditable = (fieldName) => {
    const isSuperAdmin = user?.role === 'super_admin';
    const isHRManager = user?.role === 'hr_manager';
    const isEmployee = user?.role === 'employee';

    // Super Admin can edit everything
    if (isSuperAdmin) return true;

    // HR Manager can edit most fields except sensitive ones
    if (isHRManager) {
      return !['email'].includes(fieldName); // HR Manager cannot change email
    }

    // Employees can only edit basic personal info
    if (isEmployee) {
      return ['firstName', 'lastName', 'phone'].includes(fieldName);
    }

    return true;
  };

  const validateGeneralForm = () => {
    // Role-based validation rules
    const isSuperAdmin = user?.role === 'super_admin';
    const isHRManager = user?.role === 'hr_manager';
    const isEmployee = user?.role === 'employee';

    const validationRules = {
      firstName: { required: true, minLength: 2, maxLength: 50 },
      lastName: { required: true, minLength: 2, maxLength: 50 },
      email: { required: true, email: true, maxLength: 320 },
      phone: { phone: true },
      language: { required: true },
      timezone: { required: true },
      dateFormat: { required: true },
      currency: { required: true }
    };

    // Super Admin and HR Manager can edit all fields
    // Employees can only edit their own basic info
    if (isEmployee) {
      // Employees have more restricted validation
      validationRules.firstName.maxLength = 30;
      validationRules.lastName.maxLength = 30;
    }

    const { errors, isValid } = validateForm(generalSettings, validationRules);
    setGeneralErrors(errors);
    return isValid;
  };


  const handleSaveSettings = async () => {
    // Validate forms based on active tab
    if (activeTab === 'general' && !validateGeneralForm()) {
      return;
    }

    setLoading(true);

    try {
      // Call actual API to update profile
      const result = await userService.updateProfile({
        firstName: generalSettings.firstName,
        lastName: generalSettings.lastName,
        email: generalSettings.email,
        phone: generalSettings.phone
      });

      if (result.success) {
        setHasChanges(false);
        toast.success('Settings saved successfully!');

        // Update user data in context/localStorage
        const updatedUser = { ...user, ...result.user };
        localStorage.setItem('user_data', JSON.stringify(updatedUser));

        // Reload page to reflect changes
        window.location.reload();
      } else {
        toast.error(result.error || 'Failed to save settings. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              value={generalSettings.firstName}
              onChange={handleGeneralChange}
              onKeyDown={handleGeneralKeyDown}
              disabled={!isFieldEditable('firstName')}
              maxLength={user?.role === 'employee' ? 30 : 50}
              placeholder="Enter first name"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${generalErrors.firstName ? 'border-red-500 focus:ring-red-500' : 'border-blue-300'
                } ${!isFieldEditable('firstName') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
            <ErrorMessage error={generalErrors.firstName} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              value={generalSettings.lastName}
              onChange={handleGeneralChange}
              onKeyDown={handleGeneralKeyDown}
              disabled={!isFieldEditable('lastName')}
              maxLength={user?.role === 'employee' ? 30 : 50}
              placeholder="Enter last name"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${generalErrors.lastName ? 'border-red-500 focus:ring-red-500' : 'border-blue-300'
                } ${!isFieldEditable('lastName') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
            <ErrorMessage error={generalErrors.lastName} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="text"
              name="email"
              value={generalSettings.email}
              onChange={handleGeneralChange}
              disabled={!isFieldEditable('email')}
              maxLength={320}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${generalErrors.email ? 'border-red-500 focus:ring-red-500' : 'border-blue-300'
                } ${!isFieldEditable('email') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
            <ErrorMessage error={generalErrors.email} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="text"
              name="phone"
              value={generalSettings.phone}
              onChange={handleGeneralChange}
              onKeyDown={handleGeneralKeyDown}
              disabled={!isFieldEditable('phone')}
              maxLength={10}
              placeholder="1234567890"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${generalErrors.phone ? 'border-red-500 focus:ring-red-500' : 'border-blue-300'
                } ${!isFieldEditable('phone') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
            <ErrorMessage error={generalErrors.phone} />
          </div>
        </div>
      </div>

      {/* Preferences section commented out */}
      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Preferences</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language
            </label>
            <select
              name="language"
              value={generalSettings.language}
              onChange={handleGeneralChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                generalErrors.language ? 'border-red-500 focus:ring-red-500' : 'border-blue-300'
              }`}
            >
              <option value="en">English</option>
              <option value="hi">Hindi</option>
              <option value="ta">Tamil</option>
              <option value="te">Telugu</option>
            </select>
            <ErrorMessage error={generalErrors.language} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <select
              name="timezone"
              value={generalSettings.timezone}
              onChange={handleGeneralChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                generalErrors.timezone ? 'border-red-500 focus:ring-red-500' : 'border-blue-300'
              }`}
            >
              <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">America/New_York (EST)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
            </select>
            <ErrorMessage error={generalErrors.timezone} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Format
            </label>
            <select
              name="dateFormat"
              value={generalSettings.dateFormat}
              onChange={handleGeneralChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                generalErrors.dateFormat ? 'border-red-500 focus:ring-red-500' : 'border-blue-300'
              }`}
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
            <ErrorMessage error={generalErrors.dateFormat} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Currency
            </label>
            <select
              name="currency"
              value={generalSettings.currency}
              onChange={handleGeneralChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                generalErrors.currency ? 'border-red-500 focus:ring-red-500' : 'border-blue-300'
              }`}
            >
              <option value="INR">INR (₹)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
            </select>
            <ErrorMessage error={generalErrors.currency} />
          </div>
        </div>
      </div> */}

      {/* Password section commented out */}
      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Password</h3>
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Change Password</h4>
            <p className="text-sm text-gray-500">Update your account password</p>
          </div>
          <Button
            onClick={() => setShowPasswordModal(true)}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <Key className="w-4 h-4" />
            <span>Change Password</span>
          </Button>
        </div>
      </div> */}
    </div>
  );


  const tabs = [
    { id: 'general', label: 'General', icon: User }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          </div>
          <p className="text-gray-600">Manage account</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === 'general' && renderGeneralTab()}

            {/* Save Button */}
            {hasChanges && (
              <div className="mt-8 flex justify-end">
                <Button
                  onClick={handleSaveSettings}
                  disabled={loading}
                  className="flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{loading ? 'Saving...' : 'Save'}</span>
                </Button>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Settings;